import {
  Raydium,
  Cluster,
  SignAllTransactions,
  CpmmPoolInfoLayout,
  CurveCalculator,
  WSOLMint,
  makeSwapCpmmBaseInInstruction,
  makeSwapCpmmBaseOutInstruction,
  ZERO,
  SwapResult,
} from '@raydium-io/raydium-sdk-v2';
import {
  AccountLayout,
  createAssociatedTokenAccountInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  getAssociatedTokenAddress,
  NATIVE_MINT,
  TOKEN_2022_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
  TokenAccountNotFoundError,
  TokenInvalidAccountOwnerError,
} from '@solana/spl-token';
import {
  AccountInfo,
  Connection,
  Keypair,
  PublicKey,
  SystemProgram,
  TokenAmount,
  Transaction,
} from '@solana/web3.js';
import BN from 'bn.js';
import { U64_MAX } from './config';
import { SugarMoneyProgram } from '../client';

const DEFAULT_FEE_RATE = new BN(2500);

function amountToTokenAmount(amount: string, decimals: number): TokenAmount {
  const f = new BN(amount).divn(decimals).toNumber();

  return {
    amount,
    decimals,
    uiAmount: f,
    uiAmountString: f.toString(),
  };
}

export class SugarMoneyRaydiumClient {
  private _internal: Raydium;
  private _cache: Record<string, [number, any]> = {};
  private _reserveA: Record<string, TokenAmount> = {};
  private _reserveB: Record<string, TokenAmount> = {};
  private _subs: Record<string, number[]> = {};
  private _poolKeys: Record<string, any> = {};
  private ownerPk: PublicKey;

  constructor(
    public params: {
      owner: Keypair | PublicKey;
      connection: Connection;
      cluster: Cluster;
      cpmmConfigId: PublicKey;
      cpmmProgramId: PublicKey;
      pSolMint: PublicKey;
      cacheTtl?: number;
    },
    public sugarMoneyProgram: SugarMoneyProgram,
  ) {
    params.cacheTtl = params.cacheTtl ?? 30 * 1000;
    this.ownerPk =
      Object.keys(params.owner)[0] == '_keypair'
        ? (params.owner as Keypair).publicKey
        : new PublicKey(params.owner.toString());
  }

  async init(signAllTransactions?: SignAllTransactions) {
    if (this._internal) return this._internal;
    this._internal = await Raydium.load({
      owner: this.ownerPk,
      connection: this.params.connection,
      cluster: this.params.cluster,
      disableFeatureCheck: true,
      disableLoadToken: true,
      blockhashCommitment: 'confirmed',
      signAllTransactions: signAllTransactions,
    });
    this._internal.fetchEpochInfo();
    setInterval(() => this._internal.fetchEpochInfo(), 30000);
  }

  getPoolId(mintA: PublicKey, mintB: PublicKey): PublicKey {
    [mintA, mintB] = new BN(mintA.toBuffer()).lt(new BN(mintB.toBuffer()))
      ? [mintA, mintB]
      : [mintB, mintA];

    const [poolId] = PublicKey.findProgramAddressSync(
      [
        Buffer.from('pool'),
        this.params.cpmmConfigId.toBuffer(),
        mintA.toBuffer(),
        mintB.toBuffer(),
      ],
      this.params.cpmmProgramId,
    );
    return poolId;
  }

  async subscribePool(poolId: PublicKey, cb?: (...any: any[]) => any) {
    const pidstr = poolId.toString();
    await this.unsubscribePool(poolId);

    this._subs[pidstr] = [];

    const data = await this._internal.cpmm.getPoolInfoFromRpc(pidstr);
    this._poolKeys[pidstr] = data.poolKeys;

    const sub1 = this.params.connection.onAccountChange(
      poolId,
      (accountInfo: AccountInfo<Buffer>) => {
        this._cache[pidstr] = [Date.now(), CpmmPoolInfoLayout.decode(accountInfo.data)];
        if (cb) cb(pidstr);
      },
      { commitment: 'confirmed', encoding: 'base64' },
    );
    const sub2 = this.params.connection.onAccountChange(
      new PublicKey(data.poolKeys.vault.A),
      (accountInfo: any) => {
        this._reserveA[pidstr] = amountToTokenAmount(
          AccountLayout.decode(accountInfo.data).amount.toString(),
          data.poolKeys.mintA.decimals,
        );
      },
      { commitment: 'confirmed', encoding: 'base64' },
    );
    const sub3 = this.params.connection.onAccountChange(
      new PublicKey(data.poolKeys.vault.B),
      (accountInfo: AccountInfo<Buffer>) => {
        this._reserveB[pidstr] = amountToTokenAmount(
          AccountLayout.decode(accountInfo.data).amount.toString(),
          data.poolKeys.mintB.decimals,
        );
      },
      { commitment: 'confirmed', encoding: 'base64' },
    );

    this._subs[pidstr].push(sub1, sub2, sub3);
  }

  async unsubscribePool(poolId: PublicKey) {
    const pidstr = poolId.toString();
    try {
      if (this._subs[pidstr]) {
        for (let subId of this._subs[pidstr]) {
          await this.params.connection.removeAccountChangeListener(subId);
        }
      }
    } finally {
      delete this._subs[pidstr];
      delete this._cache[pidstr];
    }
  }

  async getPoolPrice(poolId: PublicKey): Promise<number> {
    const res = await this._internal.cpmm.getPoolInfoFromRpc(poolId.toString());

    return res.rpcData.poolPrice;
  }

  async getPoolReserves(poolId: PublicKey): Promise<Record<string, TokenAmount>> {
    const pidstr = poolId.toString();
    const now = Date.now();
    const balances = {};

    if (this._cache[pidstr] && this._cache[pidstr][0] + this.params.cacheTtl > now) {
      const poolKeys = this._poolKeys[pidstr];

      balances[poolKeys.mintA.address] = this._reserveA[pidstr];
      balances[poolKeys.mintB.address] = this._reserveB[pidstr];
    } else {
      const data = await this._internal.cpmm.getPoolInfoFromRpc(pidstr);
      this._poolKeys[pidstr] = data.poolKeys;

      const res = await Promise.all([
        this.params.connection.getTokenAccountBalance(new PublicKey(data.poolKeys.vault.A)),
        this.params.connection.getTokenAccountBalance(new PublicKey(data.poolKeys.vault.B)),
      ]);

      balances[data.poolKeys.mintA.address] = res[0].value;
      balances[data.poolKeys.mintB.address] = res[1].value;
    }
    return balances;
  }

  async isUserAccountExists(mint: PublicKey, token_program = TOKEN_PROGRAM_ID): Promise<boolean> {
    try {
      let userAccount: PublicKey = await getAssociatedTokenAddress(
        mint,
        this.ownerPk,
        true,
        token_program,
      );
      await this.params.connection.getTokenAccountBalance(userAccount);
      return true;
    } catch (error: unknown) {
      console.debug(error);
      if (error.toString().indexOf('could not find account')) {
        return false;
      } else {
        throw new Error(`isUserAccountExists::${error}`);
      }
    }
  }

  async getTrade(params: {
    poolId: PublicKey;
    inputAsset: PublicKey;
    inputAmount?: BN;
    outputAmount?: BN;
    slippage?: number;
    unwrap?: boolean;
    createOutputAccount?: boolean;
  }): Promise<[Transaction, BN]> {
    let { poolId, inputAsset, inputAmount, outputAmount, slippage, unwrap, createOutputAccount } =
      params;
    if (inputAmount && outputAmount) {
      throw new Error('you can specify only the input or output amount');
    }

    const now = Date.now();
    const transaction = new Transaction();
    const pidstr = poolId.toString();

    let poolInfo;
    let poolKeys;
    let baseReserve: BN;
    let quoteReserve: BN;
    let amountInOut: BN;
    let mintA: PublicKey;
    let mintB: PublicKey;

    if (this._cache[pidstr] && this._cache[pidstr][0] + this.params.cacheTtl > now) {
      poolInfo = this._cache[pidstr][1];
      poolKeys = this._poolKeys[pidstr];
      baseReserve = new BN(this._reserveA[pidstr].amount);
      quoteReserve = new BN(this._reserveB[pidstr].amount);
    } else {
      const data = await this._internal.cpmm.getPoolInfoFromRpc(pidstr);
      poolInfo = data.poolInfo;
      poolKeys = data.poolKeys;
      baseReserve = data.rpcData.baseReserve;
      quoteReserve = data.rpcData.quoteReserve;

      if (this._subs[pidstr]) {
        this._cache[pidstr] = [now, data.poolInfo];
        this._reserveA[pidstr] = amountToTokenAmount(
          baseReserve.toString(),
          data.poolKeys.mintA.decimals,
        );
        this._reserveB[pidstr] = amountToTokenAmount(
          quoteReserve.toString(),
          data.poolKeys.mintB.decimals,
        );
      }
    }

    [mintA, mintB] = [new PublicKey(poolInfo.mintA.address), new PublicKey(poolInfo.mintB.address)];

    const isWSOL = mintA.equals(WSOLMint) || mintB.equals(WSOLMint);

    let wrapNeeded = false;
    if (inputAsset.equals(PublicKey.default)) {
      wrapNeeded = true;
      inputAsset = isWSOL ? WSOLMint : this.params.pSolMint;
    }
    const baseIn = inputAsset.equals(mintA);
    let swapResult: SwapResult;
    if (inputAmount) {
      swapResult = CurveCalculator.swap(
        inputAmount,
        baseIn ? baseReserve : quoteReserve,
        baseIn ? quoteReserve : baseReserve,
        DEFAULT_FEE_RATE,
      );

      amountInOut = swapResult.destinationAmountSwapped;
      if (!slippage) {
        swapResult.destinationAmountSwapped = ZERO;
      } else {
        swapResult.destinationAmountSwapped = swapResult.destinationAmountSwapped
          .mul(new BN(10000 - slippage))
          .div(new BN(10000));
      }
    } else {
      const res = CurveCalculator.swapBaseOut({
        poolMintA: poolInfo.mintA,
        poolMintB: poolInfo.mintB,
        tradeFeeRate: DEFAULT_FEE_RATE,
        baseReserve: baseReserve,
        quoteReserve: quoteReserve,
        outputMint: baseIn ? mintB : mintA,
        outputAmount,
      });

      swapResult = {
        sourceAmountSwapped: res.amountIn,
        destinationAmountSwapped: outputAmount,
        tradeFee: res.tradeFee,
        newSwapDestinationAmount: ZERO,
      };

      amountInOut = swapResult.sourceAmountSwapped;
      if (!slippage) {
        swapResult.sourceAmountSwapped = U64_MAX;
      } else {
        swapResult.sourceAmountSwapped = swapResult.sourceAmountSwapped
          .mul(new BN(10000 + slippage))
          .div(new BN(10000));
      }
    }

    let userInputAccount: PublicKey = await getAssociatedTokenAddress(
      baseIn ? mintA : mintB,
      this.ownerPk,
      false,
      new PublicKey(poolInfo[baseIn ? 'mintA' : 'mintB'].programId ?? TOKEN_PROGRAM_ID),
    );

    let userOutputAccount: PublicKey = await getAssociatedTokenAddress(
      baseIn ? mintB : mintA,
      this.ownerPk,
      false,
      new PublicKey(poolInfo[baseIn ? 'mintB' : 'mintA'].programId ?? TOKEN_PROGRAM_ID),
    );

    if (createOutputAccount) {
      transaction.add(
        createAssociatedTokenAccountInstruction(
          this.ownerPk,
          userOutputAccount,
          this.ownerPk,
          baseIn ? mintB : mintA,
          new PublicKey(poolInfo[baseIn ? 'mintB' : 'mintA'].programId ?? TOKEN_PROGRAM_ID),
        ),
      );
    }

    const swapIx = !!inputAmount
      ? makeSwapCpmmBaseInInstruction(
          new PublicKey(poolInfo.programId),
          this.ownerPk,
          new PublicKey(poolKeys.authority),
          new PublicKey(poolKeys.config.id),
          new PublicKey(poolInfo.id),
          userInputAccount,
          userOutputAccount,
          new PublicKey(poolKeys.vault[baseIn ? 'A' : 'B']),
          new PublicKey(poolKeys.vault[baseIn ? 'B' : 'A']),
          new PublicKey(poolInfo[baseIn ? 'mintA' : 'mintB'].programId ?? TOKEN_PROGRAM_ID),
          new PublicKey(poolInfo[baseIn ? 'mintB' : 'mintA'].programId ?? TOKEN_PROGRAM_ID),
          baseIn ? mintA : mintB,
          baseIn ? mintB : mintA,
          new PublicKey(poolKeys.observationId),

          inputAmount,
          swapResult.destinationAmountSwapped,
        )
      : makeSwapCpmmBaseOutInstruction(
          new PublicKey(poolInfo.programId),
          this.ownerPk,
          new PublicKey(poolKeys.authority),
          new PublicKey(poolKeys.config.id),
          new PublicKey(poolInfo.id),

          userInputAccount,
          userOutputAccount,

          new PublicKey(poolKeys.vault[baseIn ? 'A' : 'B']),
          new PublicKey(poolKeys.vault[baseIn ? 'B' : 'A']),
          new PublicKey(poolInfo[baseIn ? 'mintA' : 'mintB'].programId ?? TOKEN_PROGRAM_ID),
          new PublicKey(poolInfo[baseIn ? 'mintB' : 'mintA'].programId ?? TOKEN_PROGRAM_ID),
          baseIn ? mintA : mintB,
          baseIn ? mintB : mintA,

          new PublicKey(poolKeys.observationId),

          swapResult.sourceAmountSwapped,
          swapResult.destinationAmountSwapped,
        );

    transaction.add(swapIx);

    if (wrapNeeded) {
      if (isWSOL) {
        try {
          await this.params.connection.getTokenAccountBalance(userInputAccount);
          transaction.instructions.unshift(
            SystemProgram.transfer({
              fromPubkey: this.ownerPk,
              toPubkey: userInputAccount,
              lamports: swapResult.sourceAmountSwapped as unknown as bigint,
            }),
            createSyncNativeInstruction(userInputAccount),
          );
        } catch (error: unknown) {
          if (error.toString().includes('could not find account')) {
            transaction.instructions.unshift(
              createAssociatedTokenAccountInstruction(
                this.ownerPk,
                userInputAccount,
                this.ownerPk,
                NATIVE_MINT,
              ),
              SystemProgram.transfer({
                fromPubkey: this.ownerPk,
                toPubkey: userInputAccount,
                lamports: swapResult.sourceAmountSwapped as unknown as bigint,
              }),
              createSyncNativeInstruction(userInputAccount),
            );
          }
        }
      } else {
        transaction.instructions.unshift(
          await this.sugarMoneyProgram.wrapSolIx(
            swapResult.sourceAmountSwapped.eq(U64_MAX)
              ? amountInOut
              : swapResult.sourceAmountSwapped,
          ),
        );
      }
    }

    if (unwrap) {
      if (isWSOL) {
        transaction.instructions.push(
          createCloseAccountInstruction(userOutputAccount, this.ownerPk, this.ownerPk),
        );
      } else {
        transaction.instructions.push(await this.sugarMoneyProgram.unwrapSolIx(U64_MAX));
      }
    }

    return [transaction, new BN(amountInOut.toString())];
  }
}
